// The course is desktop-first: a friendly, dismissible notice on PHONES only.
//
// Mounted once in App.js (inside the router), so it shows on any signed-in page
// in SHOWN_ON (the dashboard and the learning pages) without each page carrying
// it. Device kind comes from the same coarse classifier session.signed_in uses
// (lib/deviceType): tablets and desktops never see it.
//
// "Got it" is remembered per learner (learner-scoped storage, so a second person
// on the same phone still sees it) and the notice comes back after a week.
// Records nothing: no event kind is sent for showing or dismissing it.
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { deviceType } from '@/lib/deviceType';
import { scopedKey, useLearnerScope } from '@/utils/learnerScope';

export const RESHOW_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

// Signed-in pages where the notice belongs. Auth, legal and shared pages are out.
const SHOWN_ON = [
  '/dashboard', '/learn', '/practice', '/projects', '/capstone', '/capstones',
  '/visual-flow-builder', '/flow-builder', '/profile',
];

export const isShownOn = (pathname) =>
  SHOWN_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  && !/^\/capstone\/[^/]+\/s\//.test(pathname);   // a public share link

const storageKey = () => scopedKey('notice', 'phone-dismissed');

/** True while a dismissal younger than a week is on record. */
export function isDismissed(now = Date.now()) {
  try {
    const at = Number(localStorage.getItem(storageKey()));
    return Number.isFinite(at) && at > 0 && now - at < RESHOW_AFTER_MS;
  } catch {
    return false;
  }
}

function rememberDismissal(now = Date.now()) {
  try {
    localStorage.setItem(storageKey(), String(now));
  } catch {
    /* storage unavailable: it simply shows again next visit */
  }
}

const PhoneNotice = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const scope = useLearnerScope();
  const [dismissed, setDismissed] = useState(() => isDismissed());

  // Auth lands after first paint: re-read under the real learner's key.
  useEffect(() => {
    setDismissed(isDismissed());
  }, [scope]);

  if (!user || dismissed || !isShownOn(pathname) || deviceType() !== 'phone') return null;

  const dismiss = () => {
    rememberDismissal();
    setDismissed(true);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 p-3"
      role="status"
      data-testid="phone-notice"
    >
      <div className="mx-auto max-w-md flex items-start gap-3 rounded-xl border border-blue-200 bg-white px-4 py-3 shadow-lg">
        <Monitor className="h-5 w-5 mt-0.5 flex-shrink-0 text-blue-600" />
        <p className="flex-1 text-sm text-slate-700">
          F2C Academy works best on a laptop or desktop. You can read lessons here, but building flows needs a
          bigger screen.
        </p>
        <button
          type="button"
          onClick={dismiss}
          className="flex-shrink-0 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          data-testid="phone-notice-dismiss"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

export default PhoneNotice;
